import { User, UserManager } from "oidc-client-ts";
import { type AuthenticationService } from "./types";
import { userManagerSettings } from "./logic.ts";
import { AUTH_PARAMETERS, STORAGE_KEYS } from "./data.ts";
import { bypassAuth, createMockUser } from "@/global/logic.ts";
import { FEATURE_NAMES, logInfo, logError } from "@/loggers";
import { clearAuthStorage, clearAllStorage, removeStorageItem } from "./storage-utils";
import type { useUserStore as UseUserStoreType } from "@/store/user/user";

export const instantiateAuthService = (userManager: UserManager): AuthenticationService => {
  const login = async (): Promise<void> => {
    // If authentication bypass is enabled, create and store a mock user
    if (bypassAuth) {
      const mockUser = createMockUser();
      localStorage.setItem(STORAGE_KEYS.MOCK_USER, JSON.stringify(mockUser));
      return Promise.resolve();
    }

    const currentPath = window.location.pathname;
    logInfo(FEATURE_NAMES.MVS, `Initiating IAM login redirect - current path: ${currentPath}`);

    try {
      const redirectResult = await userManager.signinRedirect({
        state: { pathname: currentPath }
      });
      logInfo(FEATURE_NAMES.MVS, `IAM login redirect initiated successfully`);
      return redirectResult;
    } catch (error) {
      logError(FEATURE_NAMES.MVS, `IAM login redirect failed: ${error}`);
    }
  };

  const isAuthenticated = async (): Promise<boolean> => {
    // If authentication bypass is enabled, always return true
    if (bypassAuth) {
      return true;
    }
    const user = await getAuthenticatedUser();
    return user !== null && !user.expired;
  };

  const getAuthenticatedUser = async (): Promise<User | null> => {
    // If authentication bypass is enabled, return a mock user
    if (bypassAuth) {
      return createMockUser();
    }
    return userManager.getUser();
  };

  const getAccessToken = async (): Promise<string | null> => {
    // If authentication bypass is enabled, return a mock token
    if (bypassAuth) {
      return "mock-access-token";
    }
    const user = await getAuthenticatedUser();
    return user?.access_token ?? null;
  };

  /**
   * Clears application-specific data during logout
   */
  const clearApplicationData = async (): Promise<void> => {
    // Remove filter selections
    removeStorageItem(STORAGE_KEYS.FILTER_SELECTIONS);

    // Pinia stores are imported dynamically here (rather than at the top of the
    // file) to avoid load-order coupling: this auth-service module is imported
    // during app bootstrap, before Pinia is installed. Static imports of stores
    // would force their modules — and any side-effectful imports they pull in —
    // to evaluate too early. Dynamic imports defer evaluation until logout time.

    // Clear user store state
    try {
      const { useUserStore } = (await import("@/store/user/user")) as { useUserStore: typeof UseUserStoreType };
      const userStore = useUserStore();
      userStore.$reset();
      logInfo(FEATURE_NAMES.MVS, "User store state cleared");
    } catch (error) {
      logError(FEATURE_NAMES.MVS, `Failed to clear user store: ${error}`);
    }

    // Clear partner-relationships cache
    try {
      const { usePartnerRelationshipsStore } = await import("@/store/partner-relationships");
      usePartnerRelationshipsStore().reset();
      logInfo(FEATURE_NAMES.MVS, "Partner-relationships store cleared");
    } catch (error) {
      logError(FEATURE_NAMES.MVS, `Failed to clear partner-relationships store: ${error}`);
    }
  };

  /**
   * Handles logout for authentication bypass mode
   */
  const handleBypassLogout = (): void => {
    logInfo(FEATURE_NAMES.MVS, "Logout in bypass mode - clearing mock authentication");
    removeStorageItem(STORAGE_KEYS.MOCK_USER);
    sessionStorage.clear();
    window.location.replace(window.location.origin);
  };

  /**
   * Clears OIDC client state
   */
  const clearOidcState = async (): Promise<void> => {
    try {
      await userManager.removeUser();

      await userManager.clearStaleState();
      logInfo(FEATURE_NAMES.MVS, "Cleared stale OIDC state");
    } catch (error) {
      // Log but don't throw - we want logout to continue
      logError(FEATURE_NAMES.MVS, `Error clearing OIDC state: ${error}`);
    }
  };

  /**
   * Constructs the IAM logout URL
   */
  const getIamLogoutUrl = (redirectUrl: string): string => {
    const baseUrl = import.meta.env.VITE_IAM_ACCOUNT as string;
    const redirectUri = redirectUrl || window.location.origin;
    return `${baseUrl}/root/auth/logout?redirect_uri=${redirectUri}`;
  };

  /**
   * Logs out the user by clearing all session data and redirecting to IAM
   * @param redirectUrl - Optional URL to redirect to after logout
   */
  const logout = async (redirectUrl = ""): Promise<void> => {
    logInfo(FEATURE_NAMES.MVS, `Logout initiated${redirectUrl ? ` with redirect URL: ${redirectUrl}` : ""}`);

    // Step 1: Clear application data
    await clearApplicationData();

    // Step 2: Handle bypass mode
    if (bypassAuth) {
      handleBypassLogout();
      return;
    }

    try {
      // Step 3: Clear auth storage
      const { errors } = clearAuthStorage();
      if (errors.length > 0) {
        errors.forEach((err) => logError(FEATURE_NAMES.MVS, err.message));
      }

      // Step 4: Clear session storage
      sessionStorage.clear();

      // Step 5: Clear OIDC state
      await clearOidcState();

      // Step 6: Redirect to IAM logout
      const logoutUrl = getIamLogoutUrl(redirectUrl);
      logInfo(FEATURE_NAMES.MVS, `Redirecting to IAM logout: ${logoutUrl}`);
      window.location.replace(logoutUrl);
    } catch (error) {
      logError(FEATURE_NAMES.MVS, `Logout error: ${error}`);

      // Fallback: Clear everything and redirect
      clearAllStorage();
      window.location.replace(window.location.origin);
    }
  };

  const isAuthenticationFlowRedirect = (search: string): boolean => {
    // If authentication bypass is enabled, never treat URLs as authentication redirects
    if (bypassAuth) {
      return false;
    }
    const searchParams = new URLSearchParams(search);
    const isAuthFlow = AUTH_PARAMETERS.every((key) => searchParams.has(key));

    if (isAuthFlow) {
      logInfo(FEATURE_NAMES.MVS, `Authentication flow redirect detected - search params: ${search}`);
    }

    return isAuthFlow;
  };

  /**
   * Process redirect callback from the OIDC provider
   * @returns the final URL to redirect to, stripped of authentication parameters
   */
  const processRedirect = async (url: string): Promise<string> => {
    // If authentication bypass is enabled, just return the URL without parameters
    if (bypassAuth) {
      const parsedUrl = new URL(url);
      return buildFinalUrl(url, parsedUrl.pathname);
    }

    try {
      const user = await userManager.signinRedirectCallback(url);
      const state = user.state as { pathname: string };
      const finalUrl = buildFinalUrl(url, state.pathname);

      logInfo(
        FEATURE_NAMES.MVS,
        `IAM redirect callback processed successfully - returning to: ${state.pathname || "root"}`
      );
      logInfo(FEATURE_NAMES.MVS, `User authenticated, Roles: ${JSON.stringify(user.profile?.roles || [])}`);

      return finalUrl;
    } catch (e) {
      logError(FEATURE_NAMES.MVS, `IAM redirect callback failed: ${e} - logging out user`);
      await logout();
    }
  };

  /**
   * Strips the authentication parameters from the URL
   * @param pathname Final destination path
   * @param url url of the page
   */
  const buildFinalUrl = (url: string, pathname: string) => {
    const parsedUrl = new URL(url);
    const params = parsedUrl.searchParams;
    AUTH_PARAMETERS.forEach((key) => params.delete(key));

    // Set the new URL without reloading the page
    return (pathname || parsedUrl.pathname) + (params.toString() ? `?${params}` : "");
  };

  return {
    login,
    isAuthenticated,
    getAuthenticatedUser,
    getAccessToken,
    logout,
    isAuthenticationFlowRedirect,
    processRedirect
  };
};

export const userManager = new UserManager(userManagerSettings);
const authService: AuthenticationService = instantiateAuthService(userManager);
export default authService;
